import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Mail, AlertCircle, XCircle, Clock, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Informational success page only. The webhook is the single source of
 * truth for fulfillment. Polling here is purely for UI feedback —
 * we never mutate any ticket state from the frontend.
 *
 * Renders one of: paid | pending | failed | expired | canceled | refunded.
 */

const STATE_VIEWS = {
  paid: {
    testid: 'success',
    Icon: CheckCircle2,
    iconBg: 'bg-[#0f231a] border-[#1f5f44]',
    iconColor: 'text-[#6EE7B7]',
    heading: 'Payment received',
    message: 'Check your email for your pass or approval status.',
  },
  pending: {
    testid: 'pending',
    Icon: Clock,
    iconBg: 'bg-[#1f1a0d] border-[#5f4a1f]',
    iconColor: 'text-[#FFB800]',
    heading: 'Payment is still processing',
    message: 'Hang tight — we’ll email your pass as soon as the bank confirms.',
  },
  failed: {
    testid: 'failed',
    Icon: AlertCircle,
    iconBg: 'bg-[#221111] border-[#5f1f1f]',
    iconColor: 'text-[#F87171]',
    heading: 'Payment was not completed',
    message: 'Please return to the event and try again.',
  },
  expired: {
    testid: 'expired',
    Icon: AlertCircle,
    iconBg: 'bg-[#221111] border-[#5f1f1f]',
    iconColor: 'text-[#F87171]',
    heading: 'Payment was not completed',
    message: 'The checkout session expired. Please return to the event and try again.',
  },
  canceled: {
    testid: 'canceled',
    Icon: XCircle,
    iconBg: 'bg-[#221111] border-[#5f1f1f]',
    iconColor: 'text-[#F87171]',
    heading: 'Payment was not completed',
    message: 'Please return to the event and try again.',
  },
  refunded: {
    testid: 'refunded',
    Icon: RotateCcw,
    iconBg: 'bg-[#1a1a1a] border-[#3a3a3a]',
    iconColor: 'text-[#AFAFAF]',
    heading: 'Payment was refunded',
    message: 'This pass is no longer valid. The refund will appear on your card within a few business days.',
  },
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const [statusInfo, setStatusInfo] = useState(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setPolling(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX = 6;
    const TERMINAL = new Set(['paid', 'failed', 'expired', 'canceled', 'refunded']);
    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatusInfo(res.data);
        const ps = (res.data?.payment_status || '').toLowerCase();
        if (TERMINAL.has(ps) || attempts >= MAX) {
          setPolling(false);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        if (attempts >= MAX) {
          setPolling(false);
          return;
        }
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const ps = (statusInfo?.payment_status || '').toLowerCase();
  // Default view = paid (for back-compat with users hitting the URL without a session_id)
  // While polling and we don't have data yet, show pending so we don't lie about a paid state.
  const viewKey = STATE_VIEWS[ps] ? ps : (sessionId && polling && !statusInfo ? 'pending' : 'paid');
  const view = STATE_VIEWS[viewKey];
  const Icon = view.Icon;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-8 text-center">
        <div className={`mx-auto w-14 h-14 rounded-full ${view.iconBg} border flex items-center justify-center mb-5`}>
          <Icon className={`w-7 h-7 ${view.iconColor}`} />
        </div>
        <h1 className="text-2xl font-bold text-[#EAE7E1] mb-2 tracking-tight" data-testid="payment-success-heading">
          {view.heading}
        </h1>
        <p className="text-[#AFAFAF] text-sm mb-6 leading-relaxed" data-testid="payment-success-message">
          {view.message}
        </p>

        {viewKey === 'paid' && (
          <div className="rounded-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 mb-6 text-left">
            <div className="flex items-center gap-2 text-xs text-[#AFAFAF] mb-1">
              <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span>What happens next</span>
            </div>
            <p className="text-[13px] text-[#EAE7E1] leading-relaxed">
              We'll email your pass automatically. If the host requires manual
              approval, you'll get a "request received" note instead, and a
              second email once you're approved.
            </p>
          </div>
        )}

        {sessionId && (
          <p className="text-[11px] text-[#555] mb-3" data-testid="payment-success-status-line">
            {polling ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Confirming with our system…
              </span>
            ) : (
              <>
                Status: <span className="text-[#AFAFAF]" data-testid="payment-success-status">{statusInfo?.payment_status || 'pending'}</span>
              </>
            )}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {(viewKey === 'paid' || viewKey === 'pending' || viewKey === 'refunded') ? (
            <Button
              onClick={() => navigate('/my-tickets')}
              className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold"
              data-testid="payment-success-my-passes-button"
            >
              Go to My Passes
            </Button>
          ) : (
            <Button
              onClick={() => navigate(-1)}
              className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold"
              data-testid="payment-success-retry-button"
            >
              Back to Event
            </Button>
          )}
          <Button
            onClick={() => navigate('/find-pass')}
            variant="outline"
            className="flex-1 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-6 py-3"
            data-testid="payment-success-find-pass-button"
          >
            Find My Pass
          </Button>
        </div>
      </div>
    </div>
  );
}
